import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

export type ValueRow = {
  code: string;
  label_ar: string;
  label_en: string;
  description_ar: string | null;
  description_en: string | null;
  emoji: string;
  color: string;
};

export type ValueContent = {
  id: string;
  kind: string;
  tier: string;
  age_group: string;
  title_ar: string;
  title_en: string;
  description_ar: string | null;
  description_en: string | null;
  cover_url: string | null;
  published_at: string | null;
};

// Public list of curriculum values (anonymous-safe).
export const listPublicValues = createServerFn({ method: "GET" }).handler(
  async (): Promise<ValueRow[]> => {
    const { data, error } = await supabaseAdmin
      .from("content_values")
      .select(
        "code,label_ar,label_en,description_ar,description_en,emoji,color",
      )
      .eq("is_active", true)
      .order("sort_order");
    if (error) {
      console.error("listPublicValues", error);
      return [];
    }
    return data ?? [];
  },
);

// Public listing of FREE published content for a given value code.
// We intentionally only expose tier='free' content to anonymous SEO pages —
// paid content stays gated by RLS for signed-in subscribers.
export const listPublicContentByValue = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z
      .object({
        code: z
          .string()
          .min(1)
          .max(64)
          .regex(/^[a-z0-9_-]+$/),
      })
      .parse(input),
  )
  .handler(
    async ({
      data,
    }): Promise<{ value: ValueRow | null; items: ValueContent[] }> => {
      const [{ data: valueRow }, { data: items }] = await Promise.all([
        supabaseAdmin
          .from("content_values")
          .select(
            "code,label_ar,label_en,description_ar,description_en,emoji,color",
          )
          .eq("code", data.code)
          .eq("is_active", true)
          .maybeSingle(),
        supabaseAdmin
          .from("content_items")
          .select(
            "id,kind,tier,age_group,title_ar,title_en,description_ar,description_en,cover_url,published_at",
          )
          .eq("status", "published")
          .eq("tier", "free")
          .contains("value_tags", [data.code])
          .order("published_at", { ascending: false })
          .limit(60),
      ]);
      return { value: valueRow ?? null, items: items ?? [] };
    },
  );
